import type React from 'react';

import { Button, PaymentMethodIcon, PaymentMethodList } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { PromoteFormContainer } from '../../../PromoteForm';

import { ShareSlideUp } from './components/ShareSlideUp';
import { usePromoteOrShareActions } from './hooks/usePromoteOrShareActions';

export const PromoteOrShareActions: React.FC<EmptyProps> = () => {
	const {
		data: {
			promoteButtonLabel,
			shareButtonLabel,
			promoteButtonUrl,
			shareButtonUrl,
			paymentMethods,
			isShareSlideUpEnabled,
			advocateEntryPointExperiment,
			advocateEntryPointUrl,
			advocateEntryPointButtonLabel,
			isInlinePromoteFormExperiment,
		},
		actions: { handlePromoteClick, handleShareClick, handleAdvocateEntryPointClick },
	} = usePromoteOrShareActions();

	return (
		<>
			<div className="flex flex-col items-center gap-3 md:gap-4">
				{isInlinePromoteFormExperiment ? (
					<PromoteFormContainer buttonLabel={promoteButtonLabel} />
				) : (
					<Button
						as="a"
						variant="primary"
						href={promoteButtonUrl}
						onClick={handlePromoteClick}
						className="w-full text-center"
						data-qa="promote-button"
					>
						{promoteButtonLabel}
					</Button>
				)}
				{advocateEntryPointExperiment ? (
					<Button
						as="a"
						variant="secondary"
						href={advocateEntryPointUrl}
						onClick={handleAdvocateEntryPointClick}
						className="w-full text-center"
						data-qa="advocate-button"
						data-testid="advocate-button"
					>
						{advocateEntryPointButtonLabel}
					</Button>
				) : (
					<>
						{isShareSlideUpEnabled && (
							/* The share slide-up is only visible on mobile. */
							<ShareSlideUp shareButtonLabel={shareButtonLabel} />
						)}
						{/* The share button is hidden on mobile to leverage the slide-up. */}
						<Button
							as="a"
							variant="secondary"
							href={shareButtonUrl}
							onClick={handleShareClick}
							className={cn('w-full text-center', { 'hidden md:block': isShareSlideUpEnabled })}
							data-qa="share-button"
							data-testid="share-button"
						>
							{shareButtonLabel}
						</Button>
					</>
				)}
			</div>
			<PaymentMethodList
				className="mx-auto grid grid-cols-4 items-center justify-items-center gap-2 md:flex md:flex-wrap md:justify-center"
				data-qa="payment-methods"
			>
				{paymentMethods.map((paymentType) => (
					// bg-white is intentional in both light and dark mode: these are trademarked payment
					// network logos that require a light backing per each network's brand guidelines, not a
					// surface that should flip with the page theme.
					<PaymentMethodIcon key={paymentType} type={paymentType} variant="card" className="bg-white" />
				))}
			</PaymentMethodList>
		</>
	);
};
